import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./ChannelRotator.css";

/**
 * Channel roller for the hero headline.
 *
 * The channel word loops forever. Only that word slides vertically: the first
 * one was just typed by the hero, so sliding it in would replay it. The words
 * around the slot never move, and the clip is done with clip-path rather than
 * overflow:hidden, because an overflow-clipped inline-block takes its
 * baseline from the bottom margin edge and drags the headline (and the typing
 * cursor next to it) out of alignment.
 *
 * words[0] must be the word already on the page when the roll starts, so the
 * hand-off from the typewriter is invisible.
 */
const ROLL_MS = 380;
const DEFAULT_LAST_WORD_DELAY = 10_000;

const ChannelRotator = ({
  prefix = "",
  suffix = "",
  words = [],
  finalPhrase = "",
  start = true,
  startDelay = 1400,
  interval = 1500,
  lastWordDelay = DEFAULT_LAST_WORD_DELAY,
  typeSpeed = 40,
  deleteSpeed = 25,
  emphasizeWord = true,
  emphasizeFinal = true,
  loopWords = false,
  loopOnce = false,
  onFinalPhraseHoldChange,
  // Fires once when a looping roll wraps back to words[0] (e.g. "ROI" -> "overload").
  onLoopComplete,
  className = "",
}) => {
  const [phase, setPhase] = useState("roll");
  const [wordIndex, setWordIndex] = useState(0);
  const [previousWordIndex, setPreviousWordIndex] = useState(null);
  const [buffer, setBuffer] = useState("");
  const [wordWidth, setWordWidth] = useState(null);
  const incomingRef = useRef(null);
  const hasLoopedRef = useRef(false);
  const completedLoopRef = useRef(false);

  const wordsKey = words.join("|");

  useEffect(() => {
    setPhase("roll");
    setWordIndex(0);
    setPreviousWordIndex(null);
    setBuffer("");
    setWordWidth(null);
    hasLoopedRef.current = false;
    completedLoopRef.current = false;
  }, [wordsKey, prefix, suffix, finalPhrase]);

  useEffect(() => {
    if (start) return;
    setPhase("roll");
    setWordIndex(0);
    setPreviousWordIndex(null);
    setBuffer("");
    setWordWidth(null);
    hasLoopedRef.current = false;
    completedLoopRef.current = false;
  }, [start]);

  // Width is animated explicitly: the clip wrapper is shrink-to-fit, so without
  // a measured width the text after the word would jump on every roll.
  useLayoutEffect(() => {
    if (phase !== "roll" || !incomingRef.current) return;
    setWordWidth(incomingRef.current.getBoundingClientRect().width);
  }, [phase, wordIndex, wordsKey]);

  // Roll through the channel words, then transition to the terminal copy.
  useEffect(() => {
    if (!start || phase !== "roll" || !words.length) return undefined;

    const isInitialWord = wordIndex === 0 && !hasLoopedRef.current;
    const delay = isInitialWord ? startDelay : interval;
    if (loopOnce && completedLoopRef.current) return undefined;
    const timeout = setTimeout(() => {
      if (wordIndex < words.length - 1 || (loopWords && !completedLoopRef.current)) {
        setPreviousWordIndex(wordIndex);
        if (loopWords && wordIndex === words.length - 1) {
          completedLoopRef.current = true;
          onLoopComplete?.();
        }
        setWordIndex((current) => (current + 1) % words.length);
        return;
      }
      setBuffer(prefix + words[wordIndex] + suffix);
      setPhase("delete");
    }, delay);

    return () => clearTimeout(timeout);
  }, [start, phase, wordIndex, words, wordsKey, prefix, suffix, startDelay, interval, loopWords, loopOnce, onLoopComplete]);

  // Drop the outgoing word once its slide has played out.
  useEffect(() => {
    if (previousWordIndex === null) return undefined;
    const timeout = setTimeout(() => setPreviousWordIndex(null), ROLL_MS);
    return () => clearTimeout(timeout);
  }, [previousWordIndex]);

  // Backspace the final channel, then type the terminal copy and hold it.
  useEffect(() => {
    if (phase === "hold") {
      onFinalPhraseHoldChange?.(true);
    } else if (phase === "reset") {
      onFinalPhraseHoldChange?.(false);
    }
  }, [phase, onFinalPhraseHoldChange]);

  useEffect(() => {
    if (phase === "delete") {
      if (!buffer) {
        setPhase("type");
        return undefined;
      }
      const timeout = setTimeout(() => setBuffer((text) => text.slice(0, -1)), deleteSpeed);
      return () => clearTimeout(timeout);
    }

    if (phase === "type") {
      if (buffer.length >= finalPhrase.length) {
        setPhase("hold");
        return undefined;
      }
      const timeout = setTimeout(
        () => setBuffer(finalPhrase.slice(0, buffer.length + 1)),
        typeSpeed,
      );
      return () => clearTimeout(timeout);
    }

    if (phase === "hold") {
      const timeout = setTimeout(() => setPhase("reset"), lastWordDelay);
      return () => clearTimeout(timeout);
    }

    if (phase === "reset") {
      if (!buffer) {
        hasLoopedRef.current = true;
        setWordIndex(0);
        setPhase("roll");
        return undefined;
      }
      const timeout = setTimeout(() => setBuffer((text) => text.slice(0, -1)), deleteSpeed);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [phase, buffer, finalPhrase, deleteSpeed, typeSpeed, lastWordDelay]);

  const wrapperClass = `channel-rotator ${className}`.trim();
  const renderWord = (word) =>
    emphasizeWord ? <em className="channel-rotator__word-text">{word}</em> : word;

  if (phase === "delete") {
    const wordStart = prefix.length;
    const wordEnd = wordStart + words[words.length - 1].length;

    return (
      <span className={wrapperClass}>
        {buffer.slice(0, Math.min(buffer.length, wordStart))}
        {renderWord(buffer.slice(wordStart, Math.min(buffer.length, wordEnd)))}
        {buffer.slice(wordEnd)}
      </span>
    );
  }

  if (phase === "type" || phase === "hold" || phase === "reset") {
    return (
      <span className={wrapperClass}>
        {emphasizeFinal ? <em className="channel-rotator__word-text">{buffer}</em> : buffer}
      </span>
    );
  }

  return (
    <span
      className={wrapperClass}
      aria-label={
        finalPhrase
          ? `${prefix}${words[wordIndex] || ""}${suffix}, ${finalPhrase}`
          : undefined
      }
    >
      {prefix}
      <span
        className="channel-rotator__slot"
        style={wordWidth === null ? undefined : { width: `${wordWidth}px` }}
      >
        {previousWordIndex !== null && (
          <span
            key={`out-${words[previousWordIndex]}`}
            className="channel-rotator__word channel-rotator__word--out"
            aria-hidden="true"
          >
            {renderWord(words[previousWordIndex])}
          </span>
        )}
        <span
          key={`in-${words[wordIndex]}`}
          ref={incomingRef}
          className={
            wordIndex === 0 && !hasLoopedRef.current
              ? "channel-rotator__word"
              : "channel-rotator__word channel-rotator__word--in"
          }
        >
          {renderWord(words[wordIndex])}
        </span>
      </span>
      {suffix}
    </span>
  );
};

export default ChannelRotator;
